/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MdmWhySection.module.scss';

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

export const MdmWhySection = () => {
  const { t } = useInt();
  const listRef = useRef<HTMLDivElement>(null);
  const hasIntersected = useIntersection(listRef, { threshold: 0.1 });

  const differentiators = [
    {
      title: t('Primary, government-sourced'),
      desc: (
        <>
          {t('Company attributes are read directly from the registry of record and 400+ official sources. You see the')}{' '}
          <strong>{t('filing behind the field')}</strong>
          {t(', rather than a value that has passed through three resellers on its way to you.')}
        </>
      ),
    },
    {
      title: t('Identity you own outright'),
      desc: (
        <>
          {t('Resolution keys on registration numbers and LEIs — public identifiers. There is')}{' '}
          <strong>{t('no proprietary ID')}</strong>{' '}
          {t('embedded in your estate to license annually or unpick if you move.')}
        </>
      ),
    },
    {
      title: t('Auditable by construction'),
      desc: (
        <>
          {t(
            'Provenance is attached at field level as data is written, not reconstructed on request. When governance asks how a value was derived,',
          )}{' '}
          <strong>{t('the lineage is already in the record')}</strong>.
        </>
      ),
    },
    {
      title: t('Redistribution rights available'),
      desc: (
        <>
          {t('If you embed company data in a product your own customers use, licensing can include')}{' '}
          <strong>{t('reseller and redistribution rights')}</strong>{' '}
          {t('— a conversation that ends most enterprise procurement cycles early elsewhere.')}
        </>
      ),
    },
    {
      title: t('Coverage where matching usually fails'),
      desc: (
        <>
          {t('Match rates hold up outside the easy jurisdictions. 200+ countries including the')}{' '}
          <strong>{t('thin-file and emerging markets')}</strong>{' '}
          {t('where supplier and customer records are hardest to resolve.')}
        </>
      ),
    },
  ];

  return (
    <Section className={styles.root}>
      <div className={cn(styles.head, hasIntersected && styles.headIn)}>
        <TitleBlock
          title={t('The difference is the source')}
          description={t(
            'Most enrichment suppliers resell a chain of upstream vendors, then wrap the result in an identifier you license. We read the registry, keep the trail, and hand you keys you already own.',
          )}
          align="left"
        />
      </div>

      <div ref={listRef} className={styles.list}>
        {differentiators.map((item, i) => (
          <div
            key={item.title}
            className={cn(styles.row, hasIntersected && styles.rowIn)}
            style={{ '--i': i } as CSSProperties}
          >
            <div className={styles.rowHead}>
              <span className={styles.rowNum}>{String(i + 1).padStart(2, '0')}</span>
              <span className={styles.tick}>
                <CheckIcon />
              </span>

              <h3 className={styles.rowTitle}>{item.title}</h3>
            </div>

            <p className={styles.rowDesc}>{item.desc}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};
